"use client";

/**
 * Auth Client - v2/Auth Worker Version
 *
 * This client handles authentication via the Auth Worker instead of Supabase.
 * It manages JWT tokens in localStorage and provides auth methods.
 */

// Auth Worker URL - use environment variable or default
const AUTH_URL = process.env.NEXT_PUBLIC_AUTH_URL || "/api/v2/auth";

// Token storage keys
const TOKEN_KEY = "solariis_token";
const REFRESH_TOKEN_KEY = "solariis_refresh_token";
const USER_KEY = "solariis_user";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "team_member" | "customer" | "talent";
  company_id: string | null;
  avatar_url: string | null;
  is_active: boolean;
  company_name: string | null;
  onboarding_status: "pending" | "completed";
}

export interface AuthSession {
  access_token: string;  // Changed from 'token' to match auth worker response
  token?: string;        // Keep for backwards compatibility
  refresh_token?: string;
  expires_at: number | string;  // Can be timestamp or ISO string
  user: AuthUser;
}

export interface AuthError {
  message: string;
  code?: string;
}

export interface AuthResult<T = unknown> {
  data: T | null;
  error: AuthError | null;
}

class AuthClient {
  private listeners: Set<(user: AuthUser | null) => void> = new Set();

  /**
   * Get the current access token from storage
   */
  getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Get the current user from storage
   */
  getUser(): AuthUser | null {
    if (typeof window === "undefined") return null;
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getUser();
  }

  /**
   * Sign in with email and password
   */
  async signInWithPassword(
    email: string,
    password: string
  ): Promise<AuthResult<AuthSession>> {
    try {
      const response = await fetch(`${AUTH_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Login failed", code: data.code },
        };
      }

      // Store tokens and user
      this.setSession(data);

      return { data, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Sign in with magic link
   */
  async signInWithMagicLink(email: string): Promise<AuthResult<{ message: string }>> {
    try {
      const response = await fetch(`${AUTH_URL}/magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          redirect_url: `${window.location.origin}/auth/callback`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Failed to send magic link" },
        };
      }

      return { data: { message: "Magic link sent" }, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Verify magic link token
   */
  async verifyMagicLink(token: string): Promise<AuthResult<AuthSession>> {
    try {
      const response = await fetch(`${AUTH_URL}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Invalid or expired token" },
        };
      }

      // Store tokens and user
      this.setSession(data);

      return { data, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Request password reset
   */
  async resetPasswordForEmail(email: string): Promise<AuthResult<{ message: string }>> {
    try {
      const response = await fetch(`${AUTH_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          redirect_url: `${window.location.origin}/auth/reset-password`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Failed to send reset email" },
        };
      }

      return { data: { message: "Reset email sent" }, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(
    token: string,
    newPassword: string
  ): Promise<AuthResult<{ message: string }>> {
    try {
      const response = await fetch(`${AUTH_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Failed to reset password" },
        };
      }

      return { data: { message: "Password reset successful" }, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<AuthResult<null>> {
    const token = this.getToken();

    // Clear local session immediately for instant redirect
    this.clearSession();

    // Fire-and-forget server logout (don't block on network)
    if (token) {
      fetch(`${AUTH_URL}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }).catch(() => {});
    }

    return { data: null, error: null };
  }

  /**
   * Refresh the access token
   */
  async refreshToken(): Promise<AuthResult<AuthSession>> {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!refreshToken) {
        return {
          data: null,
          error: { message: "No refresh token available" },
        };
      }

      const response = await fetch(`${AUTH_URL}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.clearSession();
        return {
          data: null,
          error: { message: data.error || "Failed to refresh token" },
        };
      }

      // Store new tokens
      this.setSession(data);

      return { data, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Get current user from server
   */
  async getServerUser(): Promise<AuthResult<AuthUser>> {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated" },
        };
      }

      const response = await fetch(`${AUTH_URL}/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, try to refresh
          const refreshResult = await this.refreshToken();
          if (refreshResult.data) {
            return { data: refreshResult.data.user, error: null };
          }
          this.clearSession();
        }
        return {
          data: null,
          error: { message: data.error || "Failed to get user" },
        };
      }

      // Update stored user
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      this.notifyListeners(data.user);

      return { data: data.user, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Complete onboarding with company name and optional password
   */
  async completeOnboarding(
    companyName: string,
    industry?: string,
    password?: string
  ): Promise<AuthResult<AuthUser>> {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated" },
        };
      }

      const response = await fetch(`${AUTH_URL}/complete-onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_name: companyName,
          industry,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Failed to complete onboarding" },
        };
      }

      // Update stored user
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        const userJson = encodeURIComponent(JSON.stringify({
          id: data.user.id,
          role: data.user.role,
          email: data.user.email,
          company_id: data.user.company_id,
          onboarding_status: data.user.onboarding_status,
        }));
        document.cookie = `${USER_KEY}=${userJson}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
        this.notifyListeners(data.user);
      }

      return { data: data.user, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Complete talent onboarding with profile data
   */
  async completeTalentOnboarding(
    password: string,
    areaOfExpertise: string,
    country: string,
    workUrls: string[],
    resumeKey: string
  ): Promise<AuthResult<AuthUser>> {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated" },
        };
      }

      const response = await fetch("/api/v2/talent/onboarding", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          password,
          area_of_expertise: areaOfExpertise,
          country,
          work_urls: workUrls,
          resume_key: resumeKey,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          data: null,
          error: { message: data.error || "Failed to complete talent onboarding" },
        };
      }

      // Update stored user
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        const userJson = encodeURIComponent(JSON.stringify({
          id: data.user.id,
          role: data.user.role,
          email: data.user.email,
          company_id: data.user.company_id,
          onboarding_status: data.user.onboarding_status,
        }));
        document.cookie = `${USER_KEY}=${userJson}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
        this.notifyListeners(data.user);
      }

      return { data: data.user, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Network error",
        },
      };
    }
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
    this.listeners.add(callback);

    // Immediately call with current user
    callback(this.getUser());

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Get authorization header for API requests
   */
  getAuthHeader(): Record<string, string> {
    const token = this.getToken();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }

  // Private methods

  private setSession(session: AuthSession): void {
    if (typeof window === "undefined") return;

    // Use access_token (from auth worker) or token (for backwards compatibility)
    const token = session.access_token || session.token;
    if (!token) {
      console.error("No token in session response");
      return;
    }

    // Store in localStorage for client-side access
    localStorage.setItem(TOKEN_KEY, token);
    if (session.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(session.user));

    // Also set cookies for middleware to read (httpOnly: false for client access)
    // Token cookie expires when the JWT expires
    // Handle both timestamp (number) and ISO string formats
    let expiresAtMs: number;
    if (typeof session.expires_at === "string") {
      expiresAtMs = new Date(session.expires_at).getTime();
    } else if (typeof session.expires_at === "number") {
      expiresAtMs = session.expires_at * 1000; // Convert seconds to ms
    } else {
      expiresAtMs = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days default
    }
    // Guard against clock drift — if maxAge computes to <= 0 the browser
    // silently discards the cookie and middleware can't see the token.
    const rawMaxAge = Math.floor((expiresAtMs - Date.now()) / 1000);
    const maxAge = rawMaxAge > 60 ? rawMaxAge : 7 * 24 * 60 * 60; // min 7 days fallback

    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const secureSuffix = isSecure ? '; Secure' : '';

    document.cookie = `${TOKEN_KEY}=${token}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;
    document.cookie = `${USER_KEY}=${encodeURIComponent(JSON.stringify({
      id: session.user.id,
      role: session.user.role,
      email: session.user.email,
      company_id: session.user.company_id || null,
      onboarding_status: session.user.onboarding_status || 'pending',
    }))}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;

    this.notifyListeners(session.user);
  }

  private clearSession(): void {
    if (typeof window === "undefined") return;

    // Clear localStorage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    // Clear cookies
    document.cookie = `${TOKEN_KEY}=; path=/; max-age=0`;
    document.cookie = `${USER_KEY}=; path=/; max-age=0`;

    this.notifyListeners(null);
  }

  private notifyListeners(user: AuthUser | null): void {
    this.listeners.forEach((callback) => callback(user));
  }
}

// Export singleton instance
export const authClient = new AuthClient();

// Export convenience function
export function createAuthClient(): AuthClient {
  return authClient;
}

/**
 * Inject Authorization header from localStorage token into fetch init.
 * Preserves existing headers (including FormData requests where
 * Content-Type must NOT be set manually).
 */
function injectAuth(init?: RequestInit): RequestInit {
  const token = authClient.getToken();
  if (!token) return init || {};

  const headers = new Headers(init?.headers);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}

/**
 * Fetch wrapper that automatically retries on 401 by refreshing the JWT token.
 * Use this instead of plain `fetch()` for authenticated API calls in client hooks.
 * Injects Authorization header from localStorage and retries on token refresh.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetch(input, injectAuth(init));

  if (res.status !== 401) return res;

  // Attempt token refresh
  const refreshResult = await authClient.refreshToken();
  if (!refreshResult.data) {
    // Refresh failed — return original 401 response
    return res;
  }

  // Retry with the fresh token
  return fetch(input, injectAuth(init));
}
